import React from "react";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";

const AnswerConditionModal = ({
  isOpen,
  onClose,
  answerView,
  userScore,
  maxScore,
  userRank,
  isInTimeRange,
  completedDate,
  startTime,
  endTime,
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getScorePercentage = () => {
    if (!userScore || !maxScore) return 0;
    return Math.round((userScore / maxScore) * 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isScoreConditionMet = () => {
    return getScorePercentage() >= 40;
  };

  const isRankingConditionMet = () => {
    return userRank <= 10 && isInTimeRange;
  };

  const getConditionStatus = () => {
    if (answerView === "score") {
      return isScoreConditionMet();
    } else if (answerView === "ranking") {
      return isRankingConditionMet();
    }
    return false;
  };

  const conditionMet = getConditionStatus();

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl max-w-2xl w-full mx-2 sm:mx-4 shadow-2xl transform transition-all max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                conditionMet ? "bg-green-100" : "bg-orange-100"
              }`}
            >
              {conditionMet ? (
                <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              ) : (
                <ExclamationTriangleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
              )}
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              Điều kiện xem đáp án
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <div className="space-y-4 sm:space-y-6">
            {/* Thông báo chung */}
            <div
              className={`p-3 sm:p-4 rounded-lg border-l-4 ${
                conditionMet
                  ? "bg-green-50 border-green-400"
                  : "bg-orange-50 border-orange-400"
              }`}
            >
              <div className="flex items-start">
                {conditionMet ? (
                  <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                ) : (
                  <ExclamationTriangleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                )}
                <p
                  className={`text-sm sm:text-base font-medium ${
                    conditionMet ? "text-green-800" : "text-orange-800"
                  }`}
                >
                  {conditionMet
                    ? "Chúc mừng! Bạn đã đủ điều kiện để xem đáp án."
                    : "Bạn chưa đủ điều kiện để xem đáp án."}
                </p>
              </div>
            </div>

            {/* Điều kiện theo loại */}
            {answerView === "score" && (
              <div className="space-y-3 sm:space-y-4">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 flex items-center">
                  <TrophyIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 flex-shrink-0" />
                  <span className="truncate">Điều kiện theo điểm số</span>
                </h4>
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-gray-600">
                        Điểm của bạn:
                      </span>
                      <span className="font-semibold text-gray-900 text-sm sm:text-base">
                        {userScore || 0} / {maxScore || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-gray-600">
                        Tỷ lệ phần trăm đúng:
                      </span>
                      <span
                        className={`font-semibold text-sm sm:text-base ${
                          isScoreConditionMet()
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {getScorePercentage()}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-gray-600">
                        Yêu cầu tối thiểu:
                      </span>
                      <span className="font-semibold text-gray-900 text-sm sm:text-base">
                        40%
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className={`p-2 sm:p-3 rounded-lg ${
                    isScoreConditionMet()
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  <p className="text-xs sm:text-sm font-medium">
                    {isScoreConditionMet()
                      ? "✅ Bạn đã đạt yêu cầu tối thiểu 40% để xem đáp án."
                      : "❌ Bạn cần đạt ít nhất 40% điểm số để xem đáp án."}
                  </p>
                </div>
              </div>
            )}

            {answerView === "ranking" && (
              <div className="space-y-3 sm:space-y-4">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 flex items-center">
                  <TrophyIcon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mr-2 flex-shrink-0" />
                  <span className="truncate">Điều kiện theo xếp hạng</span>
                </h4>
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-gray-600">
                        Xếp hạng của bạn:
                      </span>
                      <span
                        className={`font-semibold text-sm sm:text-base ${
                          userRank <= 10 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        #{userRank || "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-gray-600">
                        Yêu cầu tối thiểu:
                      </span>
                      <span className="font-semibold text-gray-900 text-sm sm:text-base">
                        Top 10
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                      <span className="text-xs sm:text-sm text-gray-600">
                        Thời gian nộp bài:
                      </span>
                      <span className="text-xs sm:text-sm text-gray-900">
                        {formatDate(completedDate)}
                      </span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                      <span className="text-xs sm:text-sm text-gray-600">
                        Khung thời gian thi:
                      </span>
                      <span className="text-xs sm:text-sm text-gray-900">
                        {formatDate(startTime)} - {formatDate(endTime)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div
                    className={`p-2 sm:p-3 rounded-lg ${
                      userRank <= 10
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    <p className="text-xs sm:text-sm font-medium">
                      {userRank <= 10
                        ? "✅ Bạn đã nằm trong top 10."
                        : "❌ Bạn cần nằm trong top 10 để xem đáp án."}
                    </p>
                  </div>
                  <div
                    className={`p-2 sm:p-3 rounded-lg ${
                      isInTimeRange
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    <p className="text-xs sm:text-sm font-medium">
                      {isInTimeRange
                        ? "✅ Thời gian nộp bài nằm trong khung thời gian thi."
                        : "❌ Thời gian nộp bài phải nằm trong khung thời gian thi."}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Thông tin bổ sung */}
            <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
              <div className="flex items-start">
                <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="text-xs sm:text-sm font-medium text-blue-900 mb-1">
                    Lưu ý quan trọng
                  </h5>
                  <p className="text-xs sm:text-sm text-blue-800 leading-relaxed">
                    Điều kiện xem đáp án được áp dụng để đảm bảo tính công bằng
                    và khuyến khích thí sinh cố gắng đạt kết quả tốt. Hãy tiếp
                    tục nỗ lực để đạt được điều kiện này!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors w-full sm:w-auto"
          >
            Đã hiểu
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnswerConditionModal;
